import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CompanyChangeRepository } from "@/api/company-change/repository";
import { CompanyChangeService } from "@/api/company-change/service";
import { CompanyControl, InputControl, TextareaControl } from "@/controls";
import type { ICompanyChangeRequest } from "@/types/company-change-request";
import type { ISelectOption } from "@/ui-kit";
import { Button, Checkbox, Column, Modal, Toast } from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type IProps = {
  companyType: "lender" | "contractor";
  onDismiss: () => void;
  onSubmitSuccess: (request: ICompanyChangeRequest) => void;
};

type IFormData = {
  requestedCompanyId: string;
  newCompanyName: string;
  comment: string;
};

export const RequestChangeCompanyForm = ({
  onSubmitSuccess,
  onDismiss,
  companyType,
}: IProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);

  const [companyNotListed, setCompanyNotListed] = useState<boolean>(false);

  const schema = useMemo(() => {
    if (companyNotListed) {
      return z.object({
        requestedCompanyId: z.string(),
        comment: z.string(),
        newCompanyName: z
          .string()
          .min(1, { message: t("validation.required") }),
      });
    }

    return z.object({
      requestedCompanyId: z
        .string()
        .min(1, { message: t("validation.required") }),
      comment: z.string(),
      newCompanyName: z.string().min(1, { message: t("validation.required") }),
    });
  }, [t, companyNotListed]);

  const methods = useForm<IFormData>({
    defaultValues: {
      requestedCompanyId: "",
      newCompanyName: "",
      comment: "",
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit, setValue, clearErrors } = methods;

  const onSubmit = useCallback(() => {
    handleSubmit((formData: IFormData) => {
      setLoading(true);

      const repository = new CompanyChangeRepository(
        new CompanyChangeService(),
      );
      return repository
        .requestChange({
          isOtherCompany: true,
          requestedCompanyId: companyNotListed
            ? undefined
            : Number(formData.requestedCompanyId),
          newCompanyName: formData.newCompanyName,
          comment: formData.comment ? formData.comment.trim() : "",
        })
        .then((response) => {
          onDismiss();
          onSubmitSuccess(response);
          Toast.success({
            title: t("label.changeCompany.requestHasBeenSent"),
          });
        })
        .catch(serverErrorHandler())
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [handleSubmit, companyNotListed, onDismiss, onSubmitSuccess, t]);

  // CompanyControl sets `requestedCompanyId`; the name goes with it.
  const onCompanyChange = useCallback(
    (o: ISelectOption | undefined) => {
      setValue("newCompanyName", o?.text ?? "", { shouldValidate: true });
    },
    [setValue],
  );

  const handleNotListedChange = useCallback(
    (value: boolean) => {
      setCompanyNotListed(value);
      setValue("requestedCompanyId", "");
      setValue("newCompanyName", "");
    },
    [setValue],
  );

  useEffect(() => {
    clearErrors("requestedCompanyId");
    clearErrors("newCompanyName");
  }, [companyNotListed, clearErrors]);

  return (
    <FormProvider {...methods}>
      <Modal open={true} onOpenChange={onDismiss} closeOnOutsidePress={false}>
        <Modal.Header
          title={t("label.changeCompany.requestCompanyChange")}
          description={t("label.changeCompany.description")}
        />
        <Modal.Body>
          <Column gap="sm" fullWidth>
            <CompanyControl
              name="requestedCompanyId"
              company={companyType}
              onChange={onCompanyChange}
              disabled={companyNotListed}
            />
            <Checkbox
              checked={companyNotListed}
              onChange={handleNotListedChange}
              label={t("label.changeCompany.companyNotListed")}
            />
            {companyNotListed && (
              <InputControl
                name="newCompanyName"
                label={t("label.changeCompany.companyName")}
                placeholder={t("label.changeCompany.companyNamePlaceholder")}
              />
            )}
            <TextareaControl
              name="comment"
              label={t("label.comment")}
              placeholder={t("placeholder.comment")}
            />
          </Column>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onDismiss} variant="ghost" color="neutral">
            {t("action.cancel")}
          </Button>
          <Button
            content={t("action.submit")}
            onClick={onSubmit}
            loading={loading}
          />
        </Modal.Footer>
      </Modal>
    </FormProvider>
  );
};
